import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { PlannedMeal } from './mealPlanSlice';
import type { AllergenKey } from '../data/allergens';
import type { OnboardingDiagnostics, OnboardingIdea } from '../services/onboardingWeek';

/**
 * Flow state of the Du Chef onboarding: three duels → allergies → a direction.
 *
 * Only progress lives here. The durable profile (taste axes, allergens) lives
 * in `userPreferencesSlice`, which is cloud-synced; this slice is device-local
 * and persisted so a reload mid-flow resumes at the same step.
 *
 * The generated week stays in `generatedWeek` until the finish ritual writes
 * it to the meal plan — `app/_layout.tsx` auto-completes onboarding the moment
 * any week bucket is non-empty, so nothing may touch `mealPlan.weeks` first.
 */

export type OnboardingPhase = 'quiz' | 'safety' | 'profile' | 'direction' | 'done';
export type DuelWinner = 'a' | 'b';
export type OnboardingDirection = 'plan' | 'ideas';
/** Which part of the direction screen chose: a whole card, or one of tonight's recipes (#721). */
export type OnboardingDirectionVia = 'card' | 'idea';

export interface OnboardingPick {
  question: 1 | 2 | 3;
  pairId: string;
  cardA: string;
  cardB: string;
  winner: DuelWinner;
}

export interface OnboardingSafety {
  keys: AllergenKey[];
  custom: string[];
  none: boolean;
}

interface OnboardingNudges {
  signupShown: boolean;
  signupDismissed: boolean;
  pantryReminderShown: boolean;
}

export interface OnboardingState {
  completed: boolean;
  currentPhase: OnboardingPhase;
  startedAt: string | null;
  /** Stable per-cook seed for the deck variant; set once at start. */
  seed: string | null;
  variant: 0 | 1 | 2;
  answers: DuelWinner[];
  picks: OnboardingPick[];
  safety: OnboardingSafety | null;
  generatedWeek: PlannedMeal[];
  /**
   * Recipes offered for tonight on the direction screen (#721). Optional: a
   * state persisted before they existed has no such key.
   */
  tonightIdeas?: OnboardingIdea[];
  onboardingWeekStart: string | null;
  direction: OnboardingDirection | null;
  readingCardDismissed: boolean;
  nudges: OnboardingNudges;
}

export const INITIAL_ONBOARDING_STATE: OnboardingState = {
  completed: false,
  currentPhase: 'quiz',
  startedAt: null,
  seed: null,
  variant: 0,
  answers: [],
  picks: [],
  safety: null,
  generatedWeek: [],
  tonightIdeas: [],
  onboardingWeekStart: null,
  direction: null,
  readingCardDismissed: false,
  nudges: {
    signupShown: false,
    signupDismissed: false,
    pantryReminderShown: false,
  },
};

export interface CompleteOnboardingPayload {
  mode: OnboardingDirection;
  durationMs: number;
  dinners: number;
}

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState: INITIAL_ONBOARDING_STATE,
  reducers: {
    startOnboarding(state, action: PayloadAction<{ seed: string; variant: 0 | 1 | 2; startedAt: string }>) {
      if (state.startedAt) return; // already started; a reload must not re-roll the variant
      state.seed = action.payload.seed;
      state.variant = action.payload.variant;
      state.startedAt = action.payload.startedAt;
      state.currentPhase = 'quiz';
    },
    /**
     * Records one duel. Answering an earlier question again (browser back)
     * overwrites it and drops every later answer, because questions 2 and 3
     * branch on question 1.
     */
    answerQuestion(state, action: PayloadAction<OnboardingPick>) {
      const { question } = action.payload;
      const index = question - 1;
      if (index > state.answers.length) return; // cannot skip a question
      state.answers = [...state.answers.slice(0, index), action.payload.winner];
      state.picks = [...state.picks.slice(0, index), action.payload];
      state.currentPhase = state.answers.length === 3 ? 'safety' : 'quiz';
      state.safety = state.answers.length === 3 ? state.safety : null;
    },
    confirmSafety(state, action: PayloadAction<OnboardingSafety>) {
      const { keys, custom, none } = action.payload;
      state.safety = none ? { keys: [], custom: [], none: true } : { keys, custom, none: false };
      // The profile screen reads the axes and allergies back before the week is built.
      state.currentPhase = 'profile';
    },
    /**
     * `diagnostics` never reaches the store — it rides along so the analytics
     * middleware can report why the week came out the size it did. An empty
     * `meals` array must never get here: the profile screen holds the cook on a
     * beat that names the reason instead (F1).
     */
    setGeneratedWeek(
      state,
      action: PayloadAction<{
        meals: PlannedMeal[];
        weekStart: string;
        diagnostics?: OnboardingDiagnostics;
        tonight?: OnboardingIdea[];
        /** For analytics only: the portions confirmed, and whether the stepper moved (#720). */
        people?: { count: number; changed: boolean };
      }>,
    ) {
      state.generatedWeek = action.payload.meals;
      state.tonightIdeas = action.payload.tonight ?? [];
      state.onboardingWeekStart = action.payload.weekStart;
      state.currentPhase = 'direction';
    },
    /**
     * No state change: the flow stays on the profile screen, which explains
     * itself. Exists so the analytics middleware can hear that a first week
     * could not be built, and why (F1).
     */
    recordEmptyPlan(
      _state,
      _action: PayloadAction<{ reason: 'no-recipes' | 'exclusions'; diagnostics: OnboardingDiagnostics }>,
    ) {},
    /**
     * The payload stays the bare mode; `meta.via` only tells the analytics
     * middleware whether a card or one of tonight's recipes was tapped.
     */
    chooseDirection: {
      reducer(state, action: PayloadAction<OnboardingDirection, string, { via: OnboardingDirectionVia }>) {
        state.direction = action.payload;
      },
      prepare(mode: OnboardingDirection, via: OnboardingDirectionVia = 'card') {
        return { payload: mode, meta: { via } };
      },
    },
    dismissReadingCard(state) {
      state.readingCardDismissed = true;
    },
    /** No state change: exists so the analytics middleware can hear the flip. */
    recordAxisFlip(_state, _action: PayloadAction<{ axis: 'base' | 'time' | 'heat' }>) {},
    /**
     * Payload is optional: the route guard in `app/_layout.tsx` completes
     * onboarding for existing users with no mode or timing to report.
     */
    completeOnboarding(state, _action: PayloadAction<CompleteOnboardingPayload | undefined>) {
      state.completed = true;
      state.currentPhase = 'done';
      state.generatedWeek = [];
      state.tonightIdeas = [];
    },
    dismissNudge(state, action: PayloadAction<keyof OnboardingNudges>) {
      state.nudges[action.payload] = true;
    },
    markNudgeShown(state, action: PayloadAction<keyof OnboardingNudges>) {
      state.nudges[action.payload] = true;
    },
  },
});

export const {
  startOnboarding,
  answerQuestion,
  confirmSafety,
  setGeneratedWeek,
  recordEmptyPlan,
  chooseDirection,
  dismissReadingCard,
  recordAxisFlip,
  completeOnboarding,
  dismissNudge,
  markNudgeShown,
} = onboardingSlice.actions;

type S = { onboarding: OnboardingState };
export const selectOnboardingCompleted = (state: S) => state.onboarding.completed;
export const selectOnboardingPhase = (state: S) => state.onboarding.currentPhase;
export const selectOnboardingStartedAt = (state: S) => state.onboarding.startedAt;
export const selectOnboardingSeed = (state: S) => state.onboarding.seed;
export const selectOnboardingVariant = (state: S) => state.onboarding.variant;
export const selectOnboardingAnswers = (state: S) => state.onboarding.answers;
export const selectOnboardingPicks = (state: S) => state.onboarding.picks;
export const selectOnboardingSafety = (state: S) => state.onboarding.safety;
export const selectOnboardingWeek = (state: S) => state.onboarding.generatedWeek;
const NO_IDEAS: OnboardingIdea[] = [];
export const selectOnboardingTonight = (state: S) => state.onboarding.tonightIdeas ?? NO_IDEAS;
export const selectOnboardingWeekStart = (state: S) => state.onboarding.onboardingWeekStart;
export const selectOnboardingDirection = (state: S) => state.onboarding.direction;
export const selectReadingCardDismissed = (state: S) => state.onboarding.readingCardDismissed;
export const selectOnboardingNudges = (state: S) => state.onboarding.nudges;

export default onboardingSlice.reducer;
